import React from "react";
import { useDispatch, useSelector } from "react-redux";
import allActions from "../../../actions/allActions";
import { RootState } from "../../../reducers/combineReducer";
import { FormDate } from "../../../components/form/FormDate";
import { registrationInfo } from "../../../types/types";
import { useForm } from "react-hook-form";
import FormInput from "../../../components/form/FormInput";
import { Switch } from "../../../components/utils/Switch";
import { FormSelect } from "../../../components/form/FormSelect";
import { HoverMessage } from "../../../components/utils/HoverMessage";
import { useIntl } from "react-intl";
import * as tState from "../../../reducers/toggleReducer";

export const EventInformationForm: React.FC = () => {
  const { register, errors, control } = useForm();
  const dispatch = useDispatch();
  const {
    subEventEndTime,
    subEventRegistrationEnd,
    subEventRegistrationStart,
    subEventStartTime,
    subEventUpdateEnd,
    subEventCancelEnd,
    subEventMaxRegistrants,
    subEventSupportEmail,
    subEventSupportInstruction,
    subEventSupportPhone,
    subEventName,
    subEventAllowCancel,
    subEventAllowModify,
    subEventAllowedQue,
    subEventMaxGroupSize,
  }: registrationInfo = useSelector(
    (state: RootState) =>
      state["eventState"]["registrationState"]["subEventInfo"]
  );
  const toggleState = useSelector(tState.getToggleState);
  const styles = "w-full flex flex-wrap";
  const inputContainers = "w-1/2 items-center";
  const modifyToggle = "modifyToggle";
  const cancelToggle = "cancelToggle";
  const queueToggle = "queueToggle";
  const maxGroupToggle = "maxGroupToggle";
  const cancelDateToggle = "subEventAllowCancel";
  const modifyDateToggle = "subEventAllowModify";
  const test = "testHoverHide";
  const test2 = "testHoverHide2";
  const triOptionDictionary = {
    No: "0",
    "Yes, with restriction": "1",
    "Yes, unlimited": "-1",
  };
  const handleDateChange = (e: any, date: Date, id: string) => {
    if (date) {
      dispatch(allActions.eventActions.changeRegistrationInfoDate(id, date));
    }
  };

  const resetDate = (e: any, id: string) => {
    dispatch(allActions.eventActions.changeRegistrationInfoDate(id, null));
  };

  const useFormatMessage = (messageId: string) => {
    return useIntl().formatMessage({ id: messageId });
  };

  const handleInputChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
    id: string,
    key?: string
  ) => {
    dispatch(
      allActions.eventActions.updateRegistrationInfo(id, event.target.value)
    );
    if (
      (id === cancelDateToggle || id === modifyDateToggle) &&
      event.target.value === "1"
    ) {
      dispatch(allActions.toggleActions.showToggle(id));
    } else if (id === cancelDateToggle || id === modifyDateToggle) {
      dispatch(allActions.toggleActions.hideToggle(id));
    }
  };

  const toggleElement = (id: string) => {
    if (toggleState[id]) {
      resetDateValues(id);
    }
    dispatch(allActions.toggleActions.toggle(id));
  };

  const toggleHover = (id: string) => {
    dispatch(allActions.toggleActions.toggle(id));
  };

  const resetDateValues = (id: string) => {
    if (id === cancelDateToggle) {
      dispatch(
        allActions.eventActions.updateRegistrationInfo(
          "subEventCancelEnd",
          null
        )
      );
      dispatch(
        allActions.eventActions.updateRegistrationInfo(cancelDateToggle, "0")
      );
      dispatch(allActions.toggleActions.hideToggle(cancelDateToggle));
    } else if (id === modifyDateToggle) {
      dispatch(
        allActions.eventActions.updateRegistrationInfo(
          "subEventUpdateEnd",
          null
        )
      );
      dispatch(
        allActions.eventActions.updateRegistrationInfo(modifyDateToggle, "0")
      );
      dispatch(allActions.toggleActions.hideToggle(modifyDateToggle));
    } else if (id === queueToggle) {
      dispatch(
        allActions.eventActions.updateRegistrationInfo(
          "subEventAllowedQue",
          "0"
        )
      );
    } else if (id === maxGroupToggle) {
      dispatch(
        allActions.eventActions.updateRegistrationInfo(
          "subEventMaxGroupSize",
          "0"
        )
      );
    }
  };

  return (
    <div className='w-full divide-y-2 divide-gray-300'>
      <div className='flex justify-evenly divide-x divide-gray-200'>
        <HoverMessage
          message={useFormatMessage("HoverMessage.1")}
          messageHeader={useFormatMessage("HoverMessage.1_Header")}
          id={test}
        >
          <FormInput
            value={subEventName || ""}
            question={useFormatMessage("Form.SubEvent.Name")}
            handleInputChange={handleInputChange}
            id='subEventName'
            overlayId={test}
            overlayToggle={toggleHover}
            type='text'
          />
        </HoverMessage>
      </div>
      <div className={styles}>
        <FormDate
          value={subEventStartTime ? new Date(subEventStartTime) : null}
          handleInputChange={handleDateChange}
          id='subEventStartTime'
          question={useFormatMessage("Form.SubEvent.EventStart")}
          register={register}
          errors={errors}
          control={control}
          reset={resetDate}
          containerStyle={inputContainers}
        />
        <FormDate
          value={subEventEndTime ? new Date(subEventEndTime) : null}
          handleInputChange={handleDateChange}
          id='subEventEndTime'
          question={useFormatMessage("Form.SubEvent.EventEnd")}
          register={register}
          errors={errors}
          control={control}
          reset={resetDate}
          containerStyle={inputContainers}
        />
        <FormDate
          value={
            subEventRegistrationStart
              ? new Date(subEventRegistrationStart)
              : null
          }
          handleInputChange={handleDateChange}
          id='subEventRegistrationStart'
          question={useFormatMessage("Form.SubEvent.RegistrationStart")}
          register={register}
          errors={errors}
          control={control}
          reset={resetDate}
          containerStyle={inputContainers}
        />
        <FormDate
          value={
            subEventRegistrationEnd ? new Date(subEventRegistrationEnd) : null
          }
          handleInputChange={handleDateChange}
          id='subEventRegistrationEnd'
          question={useFormatMessage("Form.SubEvent.RegistrationEnd")}
          register={register}
          errors={errors}
          control={control}
          reset={resetDate}
          containerStyle={inputContainers}
        />
      </div>
      <div className='flex justify-evenly divide-x divide-gray-200'>
        <HoverMessage
          id={test2}
          messageHeader={useFormatMessage("HoverMessage.1_Header")}
          message={useFormatMessage("HoverMessage.1")}
        >
          <div className='flex-col'>
            <Switch
              id={cancelToggle}
              header={useFormatMessage("Form.SubEvent.AllowCancel")}
              show={toggleState[cancelToggle]}
              switchAction={toggleElement}
              overlayId={test2}
              overlayToggle={toggleHover}
            />
            <FormSelect
              value={subEventAllowCancel ? subEventAllowCancel.toString() : ""}
              dictionaryOptions={triOptionDictionary}
              divStyle={toggleState[cancelToggle] ? null : "invisible"}
              handleInputChange={handleInputChange}
              id='subEventAllowCancel'
            />
            <FormDate
              value={subEventCancelEnd ? new Date(subEventCancelEnd) : null}
              handleInputChange={handleDateChange}
              id='subEventCancelEnd'
              register={register}
              divStyle={toggleState[cancelDateToggle] ? null : "invisible"}
              errors={errors}
              reset={resetDate}
              control={control}
            />
          </div>
        </HoverMessage>
        <div className='flex-col'>
          <Switch
            id={modifyToggle}
            header={useFormatMessage("Form.SubEvent.AllowModify")}
            show={toggleState[modifyToggle]}
            switchAction={toggleElement}
          />
          <FormSelect
            value={subEventAllowModify ? subEventAllowModify.toString() : ""}
            dictionaryOptions={triOptionDictionary}
            divStyle={toggleState[modifyToggle] ? null : "invisible"}
            handleInputChange={handleInputChange}
            id='subEventAllowModify'
          />
          <FormDate
            value={subEventUpdateEnd ? new Date(subEventUpdateEnd) : null}
            handleInputChange={handleDateChange}
            id='subEventUpdateEnd'
            register={register}
            divStyle={toggleState[modifyDateToggle] ? null : "invisible"}
            errors={errors}
            reset={resetDate}
            control={control}
          />
        </div>
        <div className='flex-col'>
          <Switch
            id={queueToggle}
            header={useFormatMessage("Form.SubEvent.AllowQueue")}
            show={toggleState[queueToggle]}
            switchAction={toggleElement}
          />
          <FormSelect
            value={
              subEventAllowedQue
                ? Number(subEventAllowedQue) > 0
                  ? "1"
                  : subEventAllowedQue.toString()
                : ""
            }
            dictionaryOptions={triOptionDictionary}
            divStyle={toggleState[queueToggle] ? null : "invisible"}
            handleInputChange={handleInputChange}
            id='subEventAllowedQue'
          />
          <FormInput
            value={subEventAllowedQue || ""}
            handleInputChange={handleInputChange}
            divStyle={subEventAllowedQue > 0 ? null : "invisible"}
            id='subEventAllowedQue'
            type='number'
          />
        </div>
        <div className='flex-col'>
          <Switch
            id={maxGroupToggle}
            header={useFormatMessage("Form.SubEvent.MaxGroup")}
            show={toggleState[maxGroupToggle]}
            switchAction={toggleElement}
          />
          <FormSelect
            value={
              subEventMaxGroupSize
                ? Number(subEventMaxGroupSize) > 0
                  ? "1"
                  : subEventMaxGroupSize.toString()
                : ""
            }
            dictionaryOptions={triOptionDictionary}
            divStyle={toggleState[maxGroupToggle] ? null : "invisible"}
            handleInputChange={handleInputChange}
            id='subEventMaxGroupSize'
          />
          <FormInput
            value={subEventMaxGroupSize || ""}
            handleInputChange={handleInputChange}
            divStyle={subEventMaxGroupSize > 0 ? null : "invisible"}
            id='subEventMaxGroupSize'
            type='number'
          />
        </div>
      </div>
      <div className='flex justify-evenly divide-x divide-gray-200'>
        <FormInput
          value={subEventMaxRegistrants || ""}
          question={useFormatMessage("Form.SubEvent.MaxRegistrants")}
          handleInputChange={handleInputChange}
          id='subEventMaxRegistrants'
          type='number'
        />
        <FormInput
          value={subEventSupportEmail || ""}
          question={useFormatMessage("Form.SubEvent.SupportEmail")}
          handleInputChange={handleInputChange}
          id='subEventSupportEmail'
          type='text'
        />
        <FormInput
          value={subEventSupportPhone || ""}
          question={useFormatMessage("Form.SubEvent.SupportPhone")}
          handleInputChange={handleInputChange}
          id='subEventSupportPhone'
          type='text'
        />
        <FormInput
          value={subEventSupportInstruction || ""}
          question={useFormatMessage("Form.SubEvent.SupportInstruction")}
          handleInputChange={handleInputChange}
          id='subEventSupportInstruction'
          type='text'
        />
      </div>
    </div>
  );
};

export default EventInformationForm;
